import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import {
  adSettingsApi,
  type AuctionInsightRow,
  type AuctionInsightsUploadResult,
} from '../../api/adSettingsApi';
import { adSettingsKeys } from '../../queryKeys';
import { useCanEditAdSettings } from '../../hooks';
import {
  EXPORT_STEPS,
  competitorsShowing,
  uploadInput,
  uploadSummary,
  type UploadDraft,
} from '../../utils/auctionInsightsForm';
import styles from './PaidSettings.module.css';

interface Props {
  locationId: string;
}

const EMPTY: UploadDraft = { periodStart: '', periodEnd: '', csv: '' };

const share = (
  row: AuctionInsightRow,
  field: 'impressionSharePercent' | 'overlapRatePercent' | 'outrankingSharePercent',
) => {
  const value = row[field];
  if (value === null) return '–';
  return row.underTen.includes(field) ? 'under 10%' : `${value.toFixed(1)}%`;
};

/**
 * Google Ads auction insights per campaign, from one export for all campaigns (Report Editor, Auction insights,
 * with the Campaign column): the server files each campaign under the location that owns it, so this location's
 * list below shows only its own campaigns. Jenny's recommendations read them before suggesting any cut to brand
 * spend: with no upload, she recommends checking auction insights first. Owners, admins and agency staff upload.
 */
export function AuctionInsightsCard({ locationId }: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const queryKey = adSettingsKeys.auctionInsights(locationId);
  const canEdit = useCanEditAdSettings();
  const [draft, setDraft] = useState<UploadDraft>(EMPTY);
  const [fileName, setFileName] = useState('');
  const [lastUpload, setLastUpload] = useState<AuctionInsightsUploadResult | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => adSettingsApi.listAuctionInsights(locationId),
  });

  const uploadMutation = useMutation({
    mutationFn: (input: Parameters<typeof adSettingsApi.uploadAuctionInsights>[1]) =>
      adSettingsApi.uploadAuctionInsights(locationId, input),
    onSuccess: (result) => {
      toast.success(`Saved auction insights for ${result.period.start} to ${result.period.end}.`);
      setLastUpload(result);
      setDraft(EMPTY);
      setFileName('');
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => toast.error(err.message ?? 'Could not save the export.'),
  });

  const removeMutation = useMutation({
    mutationFn: (input: { campaign: string; periodStart: string; periodEnd: string }) =>
      adSettingsApi.removeAuctionInsights(locationId, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
    onError: (err: Error) => toast.error(err.message ?? 'Could not remove the upload.'),
  });

  const readFile = (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    void file.text().then((csv) => setDraft((d) => ({ ...d, csv })));
  };

  const upload = () => {
    const result = uploadInput(draft);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    uploadMutation.mutate(result.input);
  };

  if (isLoading) {
    return (
      <Card title="Auction insights">
        <Spinner size="sm" />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card title="Auction insights">
        <p className={styles.intro}>Could not load the auction insights.</p>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </Card>
    );
  }

  return (
    <Card title="Auction insights">
      <p className={styles.intro}>
        Who else shows in each campaign&apos;s auctions. Jenny checks these before recommending any
        cut to brand spend. Upload one export for all campaigns: each campaign is filed under the
        location that owns it, for every location of this practice.
      </p>
      {canEdit && (
        <>
          <p className={styles.hint}>{EXPORT_STEPS}</p>
          <div className={styles.row}>
            <Input
              type="date"
              label="From (if not in the file)"
              value={draft.periodStart}
              onChange={(e) => setDraft({ ...draft, periodStart: e.target.value })}
            />
            <Input
              type="date"
              label="To (if not in the file)"
              value={draft.periodEnd}
              onChange={(e) => setDraft({ ...draft, periodEnd: e.target.value })}
            />
          </div>
          <input
            className={styles.file}
            type="file"
            accept=".csv,.tsv,.txt,text/csv"
            aria-label="Auction insights export"
            onChange={(e) => readFile(e.target.files?.[0])}
          />
          <p className={styles.hint}>
            {fileName ? `Ready: ${fileName}.` : 'CSV or tab-separated, as Google Ads downloads it.'}{' '}
            Upload and save stores it right away: nothing else needs saving. A new upload for the
            same period replaces each campaign&apos;s old one.
          </p>
          <div className={styles.actions}>
            <Button
              variant="primary"
              size="sm"
              onClick={upload}
              disabled={uploadMutation.isPending || !draft.csv}
            >
              {uploadMutation.isPending ? 'Saving…' : 'Upload and save'}
            </Button>
          </div>
        </>
      )}

      {lastUpload && (
        <div className={styles.summary}>
          <p className={styles.summaryHead}>
            Saved: {lastUpload.period.start} to {lastUpload.period.end}. Jenny reads it from her
            next check.
          </p>
          <ul className={styles.summaryList}>
            {uploadSummary(lastUpload.campaigns).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {data.length === 0 ? (
        <p className={styles.hint}>
          None uploaded yet, so Jenny will recommend checking auction insights before any
          brand-spend cut.
        </p>
      ) : (
        data.map((u) => (
          <section key={`${u.campaign}-${u.periodStart}-${u.periodEnd}`} className={styles.upload}>
            <div className={styles.uploadHead}>
              <span>
                {u.campaign}{' '}
                <span className={styles.muted}>
                  {u.periodStart} to {u.periodEnd} · {competitorsShowing(u.rows)} other{' '}
                  {competitorsShowing(u.rows) === 1 ? 'advertiser' : 'advertisers'}
                  {u.matchedCampaign ? '' : ' · not matched to a campaign'}
                </span>
              </span>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={removeMutation.isPending}
                  onClick={() =>
                    removeMutation.mutate({
                      campaign: u.campaign,
                      periodStart: u.periodStart,
                      periodEnd: u.periodEnd,
                    })
                  }
                >
                  Remove
                </Button>
              )}
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Impression share</th>
                    <th>Overlap rate</th>
                    <th>Outranking share</th>
                  </tr>
                </thead>
                <tbody>
                  {u.rows.map((r) => (
                    <tr key={r.domain}>
                      <td>{r.isSelf ? 'This practice' : r.domain}</td>
                      <td>{share(r, 'impressionSharePercent')}</td>
                      <td>{share(r, 'overlapRatePercent')}</td>
                      <td>{share(r, 'outrankingSharePercent')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
    </Card>
  );
}
